// /en/police-clearance-foreigner/$nationality/for/$visa — EN combo (Nationality × Thai Visa)
import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { FOREIGNER_NATIONALITIES, getForeignerNationality } from "@/data/police-foreigner-nationalities";
import { FOREIGNER_VISA_TYPES, getForeignerVisa } from "@/data/foreigner-visa-types";
import { consolidateGraph } from "@/lib/schema-graph";
import { aggregateRatingNode } from "@/lib/programmatic-ratings";
import { speakableSchema } from "@/lib/aeo";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { ContactCTA, FaqAccordion } from "@/components/seo/Blocks";
import { TrustLayer } from "@/components/TrustLayer";
import QuotableStatsStrip from "@/components/QuotableStatsStrip";
import { ShieldCheck, MessageCircle, Phone, BadgeCheck, ListChecks, AlertTriangle, FileCheck2 } from "lucide-react";

const SITE = "https://nyclanguageinstitute.com";

export const Route = createFileRoute("/en/police-clearance-foreigner/$nationality/for/$visa")({
  loader: ({ params }) => {
    if (!getForeignerNationality(params.nationality)) throw notFound();
    if (!getForeignerVisa(params.visa)) throw notFound();
    return { nSlug: params.nationality, vSlug: params.visa };
  },
  head: ({ params }) => {
    const n = getForeignerNationality(params.nationality);
    const v = getForeignerVisa(params.visa);
    if (!n || !v) return { meta: [{ title: ctaTitle("Not found", "/en/police-clearance-foreigner/$nationality/for/$visa", "en")}, { name: "robots", content: "noindex" }] };
    const url = `${SITE}/en/police-clearance-foreigner/${n.slug}/for/${v.slug}`;
    const title = `${n.homeAuthorityEn} for ${v.nameEn} — ${n.nameEn} Nationals in Thailand from THB ${n.priceFromTh.toLocaleString()} | NYC`;
    const desc = `${v.taglineEn} · ${v.timelineEn} · handled by Lawyers Council-registered attorneys. Talk to a specialist today.`;
    return {
      meta: [
        { title: ctaTitle(title, "/en/police-clearance-foreigner/$nationality/for/$visa", "en") }, { name: "description", content: ctaDesc(desc, "/en/police-clearance-foreigner/$nationality/for/$visa", "en")},
        { property: "og:title", content: ctaTitle(title, "/en/police-clearance-foreigner/$nationality/for/$visa", "en")},
        { property: "og:description", content: ctaDesc(desc, "/en/police-clearance-foreigner/$nationality/for/$visa", "en")},
        { property: "og:url", content: url },
        { property: "og:locale", content: "en_US" },
      ],
      links: [
        { rel: "canonical", href: url },
        ...hreflangLinks(`/police-clearance-foreigner/${n.slug}/for/${v.slug}`, `/en/police-clearance-foreigner/${n.slug}/for/${v.slug}`),
      ],
      scripts: [
        consolidateGraph([
          speakableSchema(),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Service",
              "@id": `${url}#service`,
              name: `${n.homeAuthorityEn} for ${v.nameEn} — ${n.nameEn} nationals`,
              serviceType: "Foreign PC + Thai Visa Support",
              areaServed: [{ "@type": "Country", name: "Thailand" }, { "@type": "Country", name: n.countryEn }],
              provider: { "@type": "LegalService", "@id": `${SITE}/#org`, name: "NYC Legal & Notary Service", telephone: "+66832494999", url: SITE },
              offers: { "@type": "Offer", price: n.priceFromTh, priceCurrency: "THB", availability: "https://schema.org/InStock" },
              aggregateRating: aggregateRatingNode(url),
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              "@id": `${url}#breadcrumb`,
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/en` },
                { "@type": "ListItem", position: 2, name: "Police Clearance", item: `${SITE}/en/police-clearance` },
                { "@type": "ListItem", position: 3, name: "Foreigners in Thailand", item: `${SITE}/en/police-clearance-foreigner` },
                { "@type": "ListItem", position: 4, name: `${n.nameEn} nationals`, item: `${SITE}/en/police-clearance-foreigner/${n.slug}` },
                { "@type": "ListItem", position: 5, name: v.nameEn, item: url },
              ],
            }),
          },
        ]),
      ],
    };
  },
  component: ComboPageEn,
});

function ComboPageEn() {
  const { nSlug, vSlug } = Route.useLoaderData() as { nSlug: string; vSlug: string };
  const n = getForeignerNationality(nSlug)!;
  const v = getForeignerVisa(vSlug)!;
  const otherVisas = FOREIGNER_VISA_TYPES.filter((x) => x.slug !== vSlug);
  const otherNats = FOREIGNER_NATIONALITIES.filter((x) => x.slug !== nSlug).slice(0, 8);

  return (
    <main className="min-h-screen bg-white">
      <section className="relative overflow-hidden bg-navy-deep text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_25%_0%,hsl(var(--gold)/0.18),transparent_55%)]" aria-hidden />
        <div className="relative mx-auto max-w-5xl px-4 py-14 sm:px-6 sm:py-20">
          <nav aria-label="Breadcrumb" className="mb-5 text-[12px] text-white/80">
            <Link to="/en" className="hover:text-gold">Home</Link><span className="px-1.5 text-white/30">/</span>
            <Link to="/en/police-clearance-foreigner" className="hover:text-gold">Foreigners in Thailand</Link><span className="px-1.5 text-white/30">/</span>
            <Link to="/en/police-clearance-foreigner/$nationality" params={{ nationality: nSlug }} className="hover:text-gold">{n.nameEn} nationals</Link><span className="px-1.5 text-white/30">/</span>
            <span className="text-white/85">{v.nameEn}</span>
          </nav>
          <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-gold">
            <ShieldCheck className="h-3.5 w-3.5" /> {v.codeTh} · {n.nameEn}
          </div>
          <h1 className="mt-4 font-display text-3xl font-extrabold leading-tight sm:text-5xl">
            {n.homeAuthorityEn} for {v.nameEn} — {n.nameEn} Nationals
          </h1>
          <p className="mt-4 max-w-3xl text-base text-white/85 sm:text-lg">
            Full evidence pack for {v.nameEn} ({v.codeTh}) — {v.taglineEn} · from THB {n.priceFromTh.toLocaleString()} · {v.timelineEn} end-to-end.
          </p>
          <div className="mt-7 flex flex-wrap gap-2.5">
            <a href="https://line.me/ti/p/@nycli" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-bold text-white shadow-md">
              <MessageCircle className="h-4 w-4" /> LINE @nycli
            </a>
            <a href="tel:0832494999" className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-bold text-navy-deep shadow-md">
              <Phone className="h-4 w-4" /> +66 83-249-4999
            </a>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6 sm:py-14">
        <blockquote className="aeo-speakable rounded-2xl border-l-4 border-gold bg-amber-50/60 p-5 text-[14px] leading-relaxed text-slate-800 sm:text-[15px]">
          <span className="mr-2 inline-flex items-center gap-1 rounded-full bg-gold/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-amber-800">
            <BadgeCheck className="h-3 w-3" /> Quotable
          </span>
          NYC Legal & Notary Service — {n.homeAuthorityEn} + {n.hague ? "Apostille" : "Embassy Legalization"} + certified Thai translation with MFA endorsement,
          filed for {v.nameEn} ({v.codeTh}) at Thai Immigration / Amphur / BOI. Turnaround {v.timelineEn} · from THB {n.priceFromTh.toLocaleString()}.
        </blockquote>

        <section className="mt-10">
          <h2 className="flex items-center gap-2 font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">
            <FileCheck2 className="h-6 w-6 text-amber-600" /> Evidence required by Thai Immigration / Amphur / BOI
          </h2>
          <ul className="mt-5 space-y-2">
            {v.keyEvidenceEn.map((e, i) => (
              <li key={i} className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-800">✓ {e}</li>
            ))}
          </ul>
        </section>

        <section className="mt-12">
          <h2 className="flex items-center gap-2 font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">
            <ListChecks className="h-6 w-6 text-amber-600" /> NYC Service Workflow
          </h2>
          <ol className="mt-5 space-y-3 text-[15px]">
            <li className="rounded-2xl border border-slate-200 bg-white p-4"><strong className="text-navy-deep">1. Free assessment:</strong> Send Passport + current visa via LINE @nycli — we identify every missing document.</li>
            <li className="rounded-2xl border border-slate-200 bg-white p-4"><strong className="text-navy-deep">2. Procure {n.homeAuthorityEn}:</strong> filed through the official {n.countryEn} channel.</li>
            <li className="rounded-2xl border border-slate-200 bg-white p-4"><strong className="text-navy-deep">3. {n.hague ? "Apostille" : "Legalization chain"}:</strong> {n.hague ? "Apostille stamp at home-country competent authority." : `Notary → Foreign Ministry (${n.countryEn}) → ${n.embassyBkkEn}.`}</li>
            <li className="rounded-2xl border border-slate-200 bg-white p-4"><strong className="text-navy-deep">4. Certified Thai translation + MFA:</strong> translated by MFA-registered translators, endorsed at the Department of Consular Affairs (Chaeng Watthana).</li>
            <li className="rounded-2xl border border-slate-200 bg-white p-4"><strong className="text-navy-deep">5. Bundle with {v.imm}:</strong> combined with all supporting evidence required by Immigration / Amphur / BOI.</li>
            <li className="rounded-2xl border border-slate-200 bg-white p-4"><strong className="text-navy-deep">6. Filing + follow-up:</strong> NYC files and tracks until {v.codeTh} is issued.</li>
          </ol>
        </section>

        <section className="mt-12">
          <h2 className="flex items-center gap-2 font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">
            <AlertTriangle className="h-6 w-6 text-rose-600" /> Common pitfalls ({v.codeTh})
          </h2>
          <ul className="mt-5 space-y-2">
            {v.pitfallsEn.map((p, i) => (
              <li key={i} className="rounded-2xl border border-rose-200 bg-rose-50/40 p-4 text-[14px] text-slate-800">⚠️ {p}</li>
            ))}
          </ul>
        </section>

        <section className="mt-12"><ContactCTA /></section>

        <section className="mt-14">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">FAQ</h2>
          <div className="mt-5">
            <FaqAccordion
              items={[
                { q: `Do I need a home-country PC for ${v.nameEn}?`, a: v.requiresPc === "always" ? "Always required." : v.requiresPc === "usually" ? "Usually required (varies by immigration office)." : "Only in specific cases." },
                { q: `How much does the package cost?`, a: `Home-country PC + ${n.hague ? "Apostille" : "Legalization"} + certified Thai translation with MFA — from THB ${n.priceFromTh.toLocaleString()}.` },
                { q: `How long does it take?`, a: `${v.timelineEn} end-to-end.` },
                { q: `Do I need to fly back to ${n.countryEn}?`, a: `Not in most cases — NYC coordinates via ${n.homeAuthorityEn} or ${n.embassyBkkEn}.` },
                { q: `How long is a PC valid?`, a: `Thai Immigration / Amphur / BOI accept PC dated within 3–6 months (varies by office).` },
              ]}
            />
          </div>
        </section>

        <section className="mt-14 border-t border-slate-200 pt-10">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">Other visas ({n.nameEn})</h2>
          <div className="mt-5 grid gap-2 sm:grid-cols-2 lg:grid-cols-2">
            {otherVisas.map((x) => (
              <Link key={x.slug} to="/en/police-clearance-foreigner/$nationality/for/$visa"
                params={{ nationality: nSlug, visa: x.slug }}
                className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 hover:border-gold/50">
                <span className="font-semibold">{x.nameEn}</span>
                <span className="ml-auto text-[10px] text-slate-500">{x.codeTh}</span>
              </Link>
            ))}
          </div>
        </section>

        <section className="mt-8">
          <h2 className="font-display text-xl font-extrabold text-navy-deep">{v.nameEn} · Other nationalities</h2>
          <div className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {otherNats.map((r) => (
              <Link key={r.slug} to="/en/police-clearance-foreigner/$nationality/for/$visa"
                params={{ nationality: r.slug, visa: vSlug }}
                className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 hover:border-gold/50">
                <span className="font-semibold">{r.nameEn}</span>
                <span className="ml-auto text-[10px] text-slate-500">THB {r.priceFromTh.toLocaleString()}</span>
              </Link>
            ))}
          </div>
        </section>
      </div>

      <TrustLayer variant="compact" />
      <QuotableStatsStrip cluster="police" locale="en" variant="light" />
    </main>
  );
}
